import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useXHR from '../../hooks/useXHR';

const REACTION_PRESETS = [
  { emoji: 'like', icon: 'fa-thumbs-up', color: '#3b82f6' },
  { emoji: 'love', icon: 'fa-heart', color: '#ef4444' },
  { emoji: 'insightful', icon: 'fa-lightbulb', color: '#f59e0b' },
  { emoji: 'bookmark', icon: 'fa-bookmark', color: '#10b981' },
  { emoji: 'star', icon: 'fa-star', color: '#eab308' },
  { emoji: 'fire', icon: 'fa-fire', color: '#F59E0B' },
  { emoji: 'question', icon: 'fa-question-circle', color: '#FDE047' },
  { emoji: 'flag', icon: 'fa-flag', color: '#047857' }
];

const TIME_LABELS = {
  morning: { icon: 'fa-sun', color: '#f59e0b', label: 'Morning' },
  midday: { icon: 'fa-cloud-sun', color: '#eab308', label: 'Midday' },
  afternoon: { icon: 'fa-sun', color: '#F59E0B', label: 'Afternoon' },
  evening: { icon: 'fa-moon', color: '#FDE047', label: 'Evening' },
  night: { icon: 'fa-moon', color: '#3b82f6', label: 'Night' }
};

export const ReactionBar = ({ resultId, existingReactions, compact }) => {
  const { token } = useAuth();
  const xhr = useXHR(token);
  const [reactions, setReactions] = useState(existingReactions || {});
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => { setReactions(existingReactions || {}); }, [existingReactions]);

  useEffect(() => {
    const handleClick = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleReaction = (emoji) => {
    xhr.post(`/search/result/${resultId}/react`, {
      body: { reaction: emoji },
      onSuccess: (data) => setReactions(data.reactions || {})
    });
  };

  const totalReactions = Object.values(reactions).reduce((sum, r) => sum + (r.count || 0), 0);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }} data-testid={`reaction-bar-${resultId}`}>
      {/* Active reactions */}
      {Object.entries(reactions).map(([emoji, data]) => {
        const preset = REACTION_PRESETS.find(p => p.emoji === emoji);
        return (
          <button
            key={emoji}
            onClick={() => toggleReaction(emoji)}
            data-testid={`reaction-${emoji}-${resultId}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 3,
              padding: '2px 6px', borderRadius: 12,
              background: data.user_reacted ? `${preset?.color || '#FCD34D'}20` : 'rgba(255, 255, 255, 0.85)',
              border: `1px solid ${data.user_reacted ? `${preset?.color || '#FCD34D'}50` : 'rgba(255,255,255,0.06)'}`,
              color: data.user_reacted ? (preset?.color || '#FCD34D') : '#71717a',
              fontSize: '0.55rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
            }}
          >
            {preset ? <i className={`fas ${preset.icon}`} style={{ fontSize: '0.5rem' }} /> : emoji}
            <span>{data.count}</span>
          </button>
        );
      })}

      {/* Add reaction button */}
      <div style={{ position: 'relative' }} ref={pickerRef}>
        <button
          onClick={() => setShowPicker(!showPicker)}
          data-testid={`add-reaction-${resultId}`}
          style={{
            padding: '2px 6px', borderRadius: 12,
            background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(255,255,255,0.06)',
            color: '#71717a', fontSize: '0.55rem', cursor: 'pointer', transition: 'all 0.15s'
          }}
        >
          <i className="fas fa-plus" style={{ fontSize: '0.45rem', marginRight: 2 }} />{!compact && totalReactions === 0 && 'React'}
        </button>

        {showPicker && (
          <div style={{
            position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
            background: 'rgba(255, 255, 255,0.98)', border: '1px solid rgba(252, 211, 77,0.4)',
            borderRadius: 10, padding: 8, display: 'flex', gap: 4, zIndex: 100,
            boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
          }}>
            {REACTION_PRESETS.map(preset => (
              <button
                key={preset.emoji}
                onClick={() => { toggleReaction(preset.emoji); setShowPicker(false); }}
                style={{
                  width: 28, height: 28, borderRadius: 6,
                  background: reactions[preset.emoji]?.user_reacted ? `${preset.color}20` : 'rgba(255, 255, 255, 0.85)',
                  border: 'none', color: preset.color, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s'
                }}
                title={preset.emoji}
              >
                <i className={`fas ${preset.icon}`} style={{ fontSize: '0.65rem' }} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const TimeOfDayBadge = ({ timeOfDay, accessCounts }) => {
  if (!timeOfDay && !accessCounts) return null;
  
  const counts = accessCounts || {};
  const primary = timeOfDay || Object.keys(counts).reduce((a, b) => (counts[a] > counts[b] ? a : b), Object.keys(counts)[0]);
  const info = TIME_LABELS[primary] || TIME_LABELS.morning;

  return (
    <span
      data-testid="time-of-day-badge"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '2px 6px', borderRadius: 10,
        background: `${info.color}15`, border: `1px solid ${info.color}30`,
        color: info.color, fontSize: '0.55rem', fontWeight: 600
      }}
      title={Object.entries(counts).map(([k, v]) => `${TIME_LABELS[k]?.label || k}: ${v}`).join(', ')}
    >
      <i className={`fas ${info.icon}`} style={{ fontSize: '0.45rem' }} />
      {info.label}
    </span>
  );
};

export default ReactionBar;
